import { Button } from "@admin/components/atoms/Button";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import { getHoursAndMinutes, Shift } from "@enos-monorepo/lib";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteTodaysAllOpenShiftsModal } from "../todays-shifts/components/Modals/DeleteTodaysAllOpenShiftsModal";
import {
  DeleteShiftModal,
  EditOpenShiftModal,
  NewOpenShiftDailyModal,
} from ".";
import { AssingOpenShiftModal } from "./CommonModals/AssignOpenShiftModal";
import { Hours } from "./Hours";

interface OpenShiftTableProps {
  loading?: boolean;
  shifts: Shift[];
  title?: string;
  isOnTodayShifts?: boolean;
  selectedDate: Date;
  companyId: string;
  selectedLocationId: string;
}

export function OpenShiftTable({
  loading,
  shifts,
  title,
  isOnTodayShifts,
  companyId,
  selectedDate,
  selectedLocationId,
}: OpenShiftTableProps) {
  const { t } = useTranslation();
  const [isDeleteTodaysShiftModalOpen, setDeleteTodaysShiftModalOpen] =
    useState(false);
  const [
    isDeleteTodaysAllOpenShiftsModalOpen,
    setDeleteTodaysAllOpenShiftsModalOpen,
  ] = useState(false);
  const [isEditTodaysOpenShiftModalOpen, setEditTodaysOpenShiftModalOpen] =
    useState(false);
  const [isAddNewOpenShiftModalOpen, setAddNewOpenShiftModalOpen] =
    useState(false);
  const [isAssignOpenShiftModalOpen, setAssignOpenShiftModalOpen] =
    useState(false);

  const [shift, setShift] = useState<Shift>();

  const columns: DataTableColumn<Shift>[] = useMemo(
    () => [
      {
        header: t("shift.Team Restrictions"),
        size: 213,
        cell({ row }) {
          return (
            <div className="font-[500] flex flex-col gap-2">
              <span>{t("shift.Open Shift")}</span>
              <span className="text-[#4B5675] text-xs">
                {t("shift.No Team Restrictions")}
              </span>
            </div>
          );
        },
      },
      {
        header: t("shift.Planned Times"),
        align: "center",
        cell({ row }) {
          return (
            <div
              onClick={() => {
                !isOnTodayShifts && setEditTodaysOpenShiftModalOpen(true);
              }}
              className={`flex justify-center ${!isOnTodayShifts ? "cursor-pointer" : ""}`}
            >
              <Hours>
                {getHoursAndMinutes(row.original.plannedStartDate)} -{" "}
                {getHoursAndMinutes(row.original.plannedEndDate)}
              </Hours>
            </div>
          );
        },
      },
      {
        header: t("common.Actions"),
        align: "center",
        size: 130,
        cell({ row }) {
          return (
            <div className="flex justify-center">
              <Button onClick={() => setAssignOpenShiftModalOpen(true)}>
                {t("shift.Assign to")}
              </Button>
            </div>
          );
        },
      },
      {
        id: "ExternalActions",
        size: 100,
        cell({ row }) {
          return (
            <div className="flex gap-2 justify-center">
              {isOnTodayShifts && (
                <i
                  className="ki-filled text-blue-500 ki-notepad-edit text-xl cursor-pointer"
                  onClick={() => setEditTodaysOpenShiftModalOpen(true)}
                ></i>
              )}
              <i
                onClick={() => setDeleteTodaysShiftModalOpen(true)}
                className="ki-filled text-red-500 ki-trash-square text-xl cursor-pointer"
              ></i>
            </div>
          );
        },
      },
    ],
    [isOnTodayShifts, t],
  );

  return (
    <>
      <DataTable
        loading={loading}
        columns={columns}
        data={shifts}
        title={title}
        onRowClick={setShift}
        header={
          <div className="flex flex-row gap-5">
            {shifts.length > 0 && (
              <Button
                variant="outline"
                property="danger"
                onClick={() => setDeleteTodaysAllOpenShiftsModalOpen(true)}
              >
                {t("shift.Delete All Open Shifts")}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => setAddNewOpenShiftModalOpen(true)}
            >
              <i className="ki-outline ki-plus-squared text-xl cursor-pointer"></i>
              {t("shift.Add New Open Shifts")}
            </Button>
          </div>
        }
      />
      <DeleteShiftModal
        open={isDeleteTodaysShiftModalOpen}
        onClose={() => setDeleteTodaysShiftModalOpen(false)}
        shift={shift}
        shiftType={
          isOnTodayShifts
            ? t("shift.Today's Open Shift")
            : t("shift.Tomorrow's Open Shift")
        }
      />
      <DeleteTodaysAllOpenShiftsModal
        open={isDeleteTodaysAllOpenShiftsModalOpen}
        onClose={() => setDeleteTodaysAllOpenShiftsModalOpen(false)}
        shifts={shifts}
      />
      <EditOpenShiftModal
        open={isEditTodaysOpenShiftModalOpen}
        onClose={() => setEditTodaysOpenShiftModalOpen(false)}
        shift={shift}
      />
      <NewOpenShiftDailyModal
        open={isAddNewOpenShiftModalOpen}
        onClose={() => setAddNewOpenShiftModalOpen(false)}
        companyId={companyId}
        companyLocationId={selectedLocationId}
        selectedDate={selectedDate}
      />
      <AssingOpenShiftModal
        open={isAssignOpenShiftModalOpen}
        onClose={() => setAssignOpenShiftModalOpen(false)}
        locationId={selectedLocationId}
        companyId={companyId}
        shift={shift}
      />
    </>
  );
}
